import type { Quality } from '@wyn/content';

/**
 * Every tunable number in the game. Starting defaults from docs/GOAL.md; the simulator
 * (packages/sim) checks pacing and inflation against these.
 */
export const MINUTE = 60_000;
export const HOUR = 60 * MINUTE;
export const DAY = 24 * HOUR;

export const balance = {
  bars: {
    life: { baseMax: 100, perLevel: 20, intervalMs: 5 * MINUTE, regenFractionOfMax: 0.05 },
    vigour: { max: 100, intervalMs: 10 * MINUTE, rate: 5 },
    /** 1 per 20 minutes, so ten Nerve take as long to refill as a full bar of Focus (docs/DECISIONS.md #119). */
    nerve: { baseMax: 10, perGuildRank: 1, cap: 60, intervalMs: 20 * MINUTE, rate: 1 },
    focus: { max: 100, intervalMs: 10 * MINUTE, rate: 5 },
    mood: { intervalMs: 15 * MINUTE, driftFractionOfBase: 0.05 },
  },
  dwellings: {
    cot: { name: 'A cot in a shared loft', moodBase: 250 },
  } as Record<string, { name: string; moodBase: number }>,
  startingDwelling: 'cot',
  training: {
    yardBoost: 1.25,
    yardBase: 1,
    /** Mood spent per point of Vigour spent. */
    moodCostPerVigour: 0.5,
    maxVigourPerAction: 1000,
  },
  start: {
    coin: 500,
    stat: 10,
    quality: 'common' satisfies Quality as Quality,
  },
  renown: {
    maxLevel: 100,
    /**
     * renownToNext(level) = round(offset + base × level^exponent). Fitted by the realm simulation so a
     * typical player reaches level 50 in about three weeks and level 100 in about six months
     * (`targets.pacing`); the offset makes even the first levels take a few actions.
     */
    offset: 40,
    base: 0.24,
    exponent: 2.2,
  },
  skills: {
    maxLevel: 100,
    /** xpToNext(level) = round(base × level^exponent) */
    base: 40,
    exponent: 1.8,
    affinityBonus: 0.15,
  },
  /**
   * What activities pay (game-core `rewards.ts`). Base XP is `cost × barWeight × (base + perLevel × level)`:
   * the same per point everywhere at a given level, so no venue is a better grind than another, and a
   * scarcer bar pays more per point. Crimes and fights pay Renown on the same scale.
   */
  rewards: {
    xpPerPoint: { base: 3, perLevel: 0.15 },
  },
  minigames: {
    /** A session left alone this long goes cold (and its row expires). */
    sessionMs: HOUR,
    renownPerXp: 0.25,
    /** Minimum quality score for crude, common, fine, superior and masterwork. */
    qualityTiers: [0, 0.35, 0.55, 0.75, 0.9],
    /** Each skill level above an activity's requirement adds this to quality, up to the cap. */
    qualityPerLevel: 0.01,
    maxQualityBonus: 0.08,
    qualityJitter: 0.02,
    /** A good's worth by quality, as a multiple of its base value. */
    qualityValue: { crude: 0.5, common: 1, fine: 1.6, superior: 2.5, masterwork: 4 } satisfies Record<Quality, number>,
    /** Slack for network and clock jitter when checking that a real-time run took real time. */
    paceGraceMs: 2000,
    /**
     * Slack for a timed move (a couched lance). The client animates only after the previous reply
     * arrives, so real time since that reply always covers the tick; this only absorbs clock jitter.
     */
    moveGraceMs: 250,
  },
  protection: { minLevel: 5, newbieHours: 72 },
  fight: {
    vigour: 25,
    /** Fights one attacker may start against the same victim in a UTC day: blocking can't stop attacks, so this limits griefing. */
    perVictimPerDay: 3,
    maxTurns: 25,
    /** A fight left alone this long goes cold: nobody is hurt further and the row expires. */
    sessionMs: HOUR,
    /** hit = base + accuracy + spread × (attack − dodge) / (attack + dodge), clamped. */
    hit: { base: 0.6, spread: 0.6, min: 0.05, max: 0.95, archeryPerLevel: 0.004 },
    /**
     * damage = weapon × scale × quality × √(Strength / start) × clamp((Strength / Defence)^exp) × (1 − armour) × jitter.
     * `scale` makes two even level-5 players with starting swords settle it in about 15 turns.
     */
    damage: { scale: 2.5, ratioExponent: 0.35, ratioMin: 0.4, ratioMax: 2.5, jitter: 0.2, maxArmour: 0.6 },
    fists: { damage: 3, accuracy: 0.05 },
    qualityMult: { crude: 0.8, common: 1, fine: 1.15, superior: 1.3, masterwork: 1.5 } satisfies Record<Quality, number>,
    /**
     * Renown for a win: `winMultiple` × what the Vigour would earn at a venue of the loser's level
     * (`rewards.fightRenown`), since an even fight is won about half the time. Spare multiplies it.
     */
    renown: { winMultiple: 2, spareMultiplier: 1.5 },
    /** Rob takes this share of the loser's purse, rolled. */
    rob: { min: 0.05, max: 0.15 },
    /** The loser's Infirmary stay, in minutes, by the winner's choice. */
    infirmaryMinutes: { spare: 15, rob: 20, maim: 60 },
    /** House Prestige a win earns (recorded now; Houses arrive in M8). */
    prestige: { spare: 1, rob: 1, maim: 3 },
  },
  crime: {
    /** Added to every crime's success chance per player level above 1. */
    successPerLevel: 0.005,
    minSuccess: 0.05,
    maxSuccess: 0.95,
  },
  gaol: {
    /** bail = ceil(minutes left) × (perMinute + perLevelPerMinute × level) */
    bailPerMinute: 4,
    bailPerLevelPerMinute: 1,
  },
  perks: {
    /** Rogue, Light Fingers. */
    rogueGaolTime: 0.75,
    /** Cloistered, Sanctuary: own Infirmary time. */
    cloisteredInfirmaryTime: 0.75,
    /** Ranger, Woodcraft: chance of being caught poaching. */
    rangerPoachingCatch: 0.5,
    /** Knight, Chivalry: Spare's Renown, and Defence in a fight. */
    knightSpareRenown: 2,
    knightDefence: 1.05,
    /** Ranger, Woodcraft: Dexterity in a fight. */
    rangerDexterity: 1.05,
    /** Rogue, Light Fingers: added to the share Rob takes. */
    rogueRob: 0.03,
    /** Artisan, Thrift: chance a craft consumes no materials. */
    artisanThrift: 0.1,
  },
  shop: { maxPerPurchase: 10 },
  consumables: {
    /** What a consumable does scales with its quality. */
    qualityMult: { crude: 0.75, common: 1, fine: 1.15, superior: 1.3, masterwork: 1.5 } satisfies Record<Quality, number>,
    /** Vigour, Focus and Nerve can be lifted to this multiple of their max, and no further. */
    barCapMultiple: 2,
    /** Ale and feasts lift Mood above the dwelling's base, up to this. */
    moodCap: 1000,
  },
  calling: {
    /** The Herald's Office is in the capital. */
    town: 'wynchester' as const,
    cooldownDays: 30,
    /** fee = base + perLevel × level, from the purse. */
    fee: { base: 250, perLevel: 50 },
  },
  bank: {
    town: 'wynchester' as const,
    /** Fixed interest for the whole term, by days locked. Paid with the principal at the first rollover after it matures. */
    lockRates: { 7: 0.01, 14: 0.025, 30: 0.06 } as Record<number, number>,
    minLock: 100,
    maxLock: 1_000_000,
    maxLocks: 3,
  },
  market: {
    /** Taken from the seller's proceeds; the Merchant's Trade Licence lowers it. */
    fee: 0.05,
    merchantFee: 0.04,
    maxOpenOrders: 20,
    maxPrice: 1_000_000,
    maxQuantity: 1000,
  },
  trade: {
    /** An offer nobody answers lapses after this. */
    offerMs: DAY,
    maxOpenOffers: 10,
    maxLines: 6,
  },
  realtime: {
    /** Chat lines are kept this long (the rows carry a TTL). */
    chatKeepMs: 30 * DAY,
    chatMaxLength: 280,
    /** Topics one socket may follow at once: the realm, a town and a few books. */
    maxTopics: 8,
  },
  /** Per-player token buckets. Each mutating action takes one token. */
  rateLimits: {
    action: { capacity: 20, refillPerSecond: 1 },
    /** Moves within a minigame (each dig, each roll) come thick and fast. */
    minigame: { capacity: 30, refillPerSecond: 3 },
    /** Each turn of a fight is a request. */
    fight: { capacity: 30, refillPerSecond: 3 },
    /** A line of chat: a short burst, then one every two seconds. */
    chat: { capacity: 5, refillPerSecond: 0.5 },
    /** Changing what a socket follows writes a row per topic. */
    socket: { capacity: 10, refillPerSecond: 0.2 },
    /** Reports: a few at once, then one every five minutes. Each target can be reported only once anyway. */
    report: { capacity: 5, refillPerSecond: 1 / 300 },
  },
  names: { min: 3, max: 20 },
  /**
   * What the balance tooling (packages/sim, docs/BALANCE.md) holds the numbers above to. CI fails
   * when a simulated realm falls outside these bands.
   */
  targets: {
    /** The median minigame score of a typical player. Every default yield and simulation assumes it. */
    typicalScore: 0.7,
    /**
     * Days to player level (Renown) for a realistic player at the typical score. An optimiser may get
     * there faster, but by no more than `optimiserMaxSpeedup`; anything quicker is an exploit.
     */
    pacing: { level50Days: [14, 28], level100Days: [150, 210], optimiserMaxSpeedup: 2 },
    /**
     * After `fromDay`, sinks must take back at least this share of the coin faucets make. (A growth band
     * on the coin supply can't be met in a new realm: with any steady income, supply grows by about
     * 1/day a day. It needs sinks that scale with wealth, such as upkeep; docs/DECISIONS.md #124.)
     */
    inflation: { minSinkShare: 0.75, fromDay: 7 },
    /**
     * Within a Discipline, at the same skill level, the best venue's Renown per bar point (each venue at
     * its best open tier) may be at most this multiple of the median venue's.
     */
    dominance: { maxYieldRatio: 1.5 },
    /** Every game of chance keeps at least this share of what is staked, at the typical score. */
    gambling: { minHouseEdge: 0.02 },
    /** Win rates of the six Callings in even fights may differ by at most this much. */
    callings: { maxWinRateSpread: 0.1 },
  },
} as const;

export type Balance = typeof balance;
